<template lang="pug">
.insights-header.pb-10
  .insights-header-content
    .d-flex.align-items-center.justify-space-between.flex-auto.flex-wrap.gap-10
      .d-flex.align-items-center.justify-space-between.mb-10.flex-1.flex-wrap.gap-10
        .text-24.mr-10.font-thin {{ pageTitle }}
      .d-flex.align-items-center.mb-10.flex-wrap.gap-10
        a.what-is-ua-btn.btn.btn-default(
          v-if="isUnusualActivityTab"
          role="button"
          data-testid="insights-what-is-ua-button"
          @click="openWhatIsUaDialog"
        )
          i.fa.fa-question-circle-o
          | What is unusual activity?

        //- Deliberately NOT .smart-notifications-btn: _insights.scss forces that
        //- class transparent when not hovered, which blanks a solid primary button.
        //- Outline variant in the locked state, where the upsell banner owns the primary CTA.
        a.create-notification-btn.btn(
          v-if="displaySmartNotifications && isUnusualActivityTab"
          :class="isNoInsightsUaTab ? 'btn-default create-notification-btn--outline' : 'btn-primary'"
          :href="smartNotificationsSettingsUrl"
          data-testid="insights-create-notification-button"
          @click="trackClick"
        )
          i.fa.fa-bell-o
          | Create notification

        a.settings-link.smart-notifications-btn.btn.btn-outline-primary.line-height-1rem.px-10.py-7(
          v-if="displaySmartNotifications && !isUnusualActivityTab"
          :href="smartNotificationsSettingsUrl"
          data-testid="insights-smart-notifications-button"
          @click="trackClick"
        )
          i.hi.hi-bell.mr-5
          | Smart notifications

        AppDropdown(
          v-if="exportMenuAvailable"
          :key="`export-menu-${remoteVsOffice}`"
          direction="left"
        )
          template(#trigger)
            a.settings-link.smart-notifications-btn.btn.btn-outline-primary.line-height-1rem.px-10.py-7.dropdown-toggle(
              role="button"
              data-toggle="dropdown"
              data-testid="insights-export-trigger"
            )
              i.hi.hi-export.mr-5
              | Export
              i.hi.hi-caret.ml-5
          AppDropdownItem(
            v-if="performanceCSVEnabled"
            :disabled="exporting"
            data-testid="performance-export-csv"
            @click="!exporting && downloadPerformanceCSV()"
          ) To CSV
          AppDropdownItem(
            v-if="performancePDFEnabled"
            v-tooltip="{ html: true }"
            :title="pdfExportTooltip"
            :disabled="remoteVsOffice || exporting"
            data-testid="performance-export-pdf"
            @click="!remoteVsOffice && !exporting && downloadPerformancePDF()"
          ) To PDF

        a.settings-link.btn.btn-outline-primary.line-height-1rem.qa-insight-settings.min-w-0.p-7(
          v-if="displaySettings"
          v-tooltip
          :class="{ 'insights-header-icon-btn--lg': isUnusualActivityTab }"
          :href="insightsSettingsUrl"
          title="Settings"
        )
          i.hi.hi-settings

        a.settings-link.btn.btn-outline-primary.line-height-1rem.insights-navigation.min-w-0.p-7(
          v-if="displaySidebar"
          :key="`${showNavigationSidebar}`"
          v-tooltip="tooltipOptions"
          :title="showNavigationSidebar ? 'Hide sidebar' : 'Show sidebar'"
          :class="{ hover: showNavigationSidebar, 'insights-header-icon-btn--lg': isUnusualActivityTab }"
          data-testid="insights-sidebar-toggle"
          @click="toggleNavigationSidebar()"
        )
          i.fa.fa-list

    BackgroundTaskStatusMountpoint
    AppExporterWebsockets(ref="exporterWs" :key="exporterWsKey" @complete="onExportComplete")

    div(v-if="!isNoInsightsUaTab")
      template(v-if="canSeeTeam")
        .d-inline-flex.align-items-center.mb-10.flex-auto.cursor-pointer.gap-5.py-5(
          v-if="hasFilters"
          @click="isTimeline ? timelineBackButtonLink() : backButtonLink()"
        )
          i.hi.hi-left-arrow.text-lighter
          | {{ backButtonText }}

      template(v-if="isTimeline")
        InsightsHeaderTimeline(
          :value="{ from: filters.date.from, to: filters.date.to }"
          :user="timelineUser"
          :start-week-on="startWeekOn"
          :disabled="isTimelineLoading"
          @date="setDateFilter"
          @timezone="changeTimeZone"
          @member="(member) => handleTimelineInput('user', member)"
          @team="(team) => handleTimelineInput('team', team)"
        )

      template(v-else)
        .grid
          template(v-if="canSeeTeam")
            InsightsLazyMembersSelect(
              data-testid="insights-member-select"
              @input="handleLazyInput"
            )

          .insights-header-datetime(:class="{ 'can-see-team': canSeeTeam }")
            InsightsHeaderDateFilter.insights-header-date-filter(
              :settings="filterSettings"
              :value="{ from: filters.date.from, to: filters.date.to }"
              :start-week-on="startWeekOn"
              :disabled="dateFilterDisabled"
              @input="setDateFilter"
            )

            .d-flex.align-items-center.gap-5.insights-header-timezone
              InsightsHeaderTimeZoneFilter(
                direction="right"
                trigger-classes="btn-default h-40"
                show-description
                :disabled="dateFilterDisabled"
                :available="isTimeZoneDropDownAvailable"
                @input="changeTimeZone"
              )
              .d-flex.align-items-center.datefilter-info
                i.hi.hi-12.hi-info-filled.text-primary.ml-8.mr-5(
                  v-tooltip="tooltipInfoOptions"
                  :title="tooltipInfoContent"
                )

  InsightsCTA.mt-30(v-if="!isNoInsightsUaTab")

  InsightsUnusualActivityInfoDialog(
    v-if="showWhatIsUaDialog"
    :show="showWhatIsUaDialog"
    @close="showWhatIsUaDialog = false"
  )
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';

import InsightsCTA from '~/components/insights/InsightsCTA.vue';
import InsightsHeaderDateFilter from '~/components/insights/InsightsHeaderDateFilter.vue';
import InsightsHeaderTimeZoneFilter from '~/components/insights/InsightsHeaderTimeZoneFilter.vue';
import InsightsLazyMembersSelect from '~/components/insights/InsightsLazyMembersSelect.vue';
import { InsightsFilters } from '~/mixins';
import InsightsHeaderTimeline from '~/components/insights/InsightsHeaderTimeline.vue';
import InsightsUnusualActivityInfoDialog from '~/components/insights/InsightsUnusualActivityInfoDialog.vue';
import AppDropdown from '~/components/AppDropdown.vue';
import AppDropdownItem from '~/components/AppDropdownItem.vue';
import AppExporterWebsockets from '~/components/AppExporterWebsockets.vue';
import BackgroundTaskStatusMountpoint from '~/components/BackgroundTaskStatusMountpoint.vue';
import { INSIGHTS } from '~/app_constants';
import get from 'lodash-es/get';

export default {
  name: 'InsightsHeader',
  components: {
    AppDropdown,
    AppDropdownItem,
    AppExporterWebsockets,
    BackgroundTaskStatusMountpoint,
    InsightsCTA,
    InsightsHeaderTimeline,
    InsightsHeaderDateFilter,
    InsightsHeaderTimeZoneFilter,
    InsightsLazyMembersSelect,
    InsightsUnusualActivityInfoDialog,
  },
  mixins: [InsightsFilters],
  props: {
    tab: {
      type: String,
      required: true,
    },
    timelineLoading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['timeline-user-selected', 'timeline-team-selected', 'timeline-all-selected'],
  data: () => ({
    exporting: false,
    showWhatIsUaDialog: false,
    tooltipInfoOptions: Object.freeze({ html: true, sanitize: true }),
    tooltipInfoContent: 'Max date range: 2 months',
    tooltipOptions: Object.freeze({ placement: 'left' }),
  }),
  computed: {
    ...mapState({
      filters: (state) => state.filters,
    }),
    ...mapGetters([
      'hasUsers',
      'startWeekOn',
      'isEverythingReady',
      'insightsSettingsUrl',
      'isTimeZoneDropDownAvailable',
      'widgets',
      'timeZone',
    ]),
    ...mapGetters([
      'canSeeTeam',
      'insightsAvailable',
      'isManager',
      'isOwner',
      'isTeamLead',
      'hasNoInsightsUaPreview',
      'organization',
      'organizationId',
      'showNavigationSidebar',
      'smartNotificationsSettingsUrl',
      'experimentImages',
      'performancePDFEnabled',
      'performanceCSVEnabled',
      'performancePDFUrl',
      'performanceCSVUrl',
      'remoteVsOffice',
      'leaderboardShowMetrics',
      'showBenchmarksAverages',
    ]),
    isUnusualActivityTab() {
      return this.tab === 'unusual-activity';
    },
    isNoInsightsUaTab() {
      return this.isUnusualActivityTab && this.hasNoInsightsUaPreview;
    },
    displaySettings() {
      return !!this.insightsSettingsUrl && !this.isTimeline;
    },
    displaySidebar() {
      return this.canSeeTeam && this.insightsAvailable && !this.isTimeline;
    },
    displaySmartNotifications() {
      return (this.isOwner || this.isManager || this.isTeamLead) && this.smartNotificationsSettingsUrl;
    },
    hasFilters() {
      return (this.filters.user_id && this.filters.user_id !== 'all') || this.filters.team_id;
    },
    isTimeline() {
      return this.tab === 'timeline';
    },
    pdfExportTooltip() {
      return this.remoteVsOffice ? 'Unable to export<br>Remote vs in-office page' : '';
    },
    exportMenuAvailable() {
      return this.tab === 'performance' && (this.performancePDFEnabled || this.performanceCSVEnabled);
    },
    pageTitle() {
      const titles = {
        'unusual-activity': 'Unusual activity',
        highlights: 'Highlights',
        output: 'Output',
        performance: 'Performance',
        timeline: 'Timeline',
      };

      return titles[this.tab] || 'Insights';
    },
    userId() {
      return this.filters?.user_id;
    },
    teamId() {
      return this.filters?.team_id;
    },
    timelineWidgetKey() {
      return this.userId ? INSIGHTS.api.insightsTimeline.user : INSIGHTS.api.insightsTimeline.team;
    },
    timelineWidgetData() {
      return get(this.widgets, this.timelineWidgetKey, {});
    },
    timelineUser() {
      // Extract user data from timeline widget for timeline header
      return this.timelineWidgetData.user || this.timelineWidgetData.data?.user || null;
    },
    // Check if the timeline chart is currently loading
    isTimelineLoading() {
      return this.timelineLoading;
    },
    dateFilterDisabled() {
      return !this.isEverythingReady;
    },
    exporterWsKey() {
      return "exporterWsKey"
    }
  },
  methods: {
    ...mapActions(['toggleNavigationSidebar']),
    onExportComplete() {
      this.exporting = false;
    },
    trackClick() {
      window.analytics?.track('Smart notifications selected');
    },
    openWhatIsUaDialog() {
      window.analytics?.track('What is unusual activity viewed', { source: 'ua_header' });
      this.showWhatIsUaDialog = true;
    },
    async downloadPerformancePDF() {
      if (this.$refs.exporterWs?.isQueued) {
        return;
      }

      const params = this.buildPDFQueryParams();
      const query = new URLSearchParams(params).toString();
      const url = query ? `${this.performancePDFUrl}?${query}` : this.performancePDFUrl;
      const fullUrl = `${window.location.origin}${url}`;

      this.exporting = true;
      try {
        let memberFilterScope;

        if (this.userId && this.userId !== 'all') {
          memberFilterScope = 'Single user';
        } else if (this.teamId) {
          memberFilterScope = 'Team';
        } else {
          memberFilterScope = 'All members';
        }

        window.analytics?.track('export pdf clicked', { member_filter_scope: memberFilterScope });
        await this.$refs.exporterWs.download('pdf', { url: fullUrl, background: true });
      } catch {
        this.exporting = false;
      }
    },
    async downloadPerformanceCSV() {
      if (this.$refs.exporterWs?.isQueued) {
        return;
      }

      const params = this.buildCSVQueryParams();
      const query = new URLSearchParams(params).toString();
      const url = query ? `${this.performanceCSVUrl}?${query}` : this.performanceCSVUrl;
      const fullUrl = `${window.location.origin}${url}`;

      this.exporting = true;
      try {
        await this.$refs.exporterWs.download('csv', { url: fullUrl, background: true });
      } catch {
        this.exporting = false;
      }
    },
    buildCSVQueryParams() {
      const params = {
        from: this.filters.date?.from,
        to: this.filters.date?.to,
        time_zone: this.filters.time_zone || this.organization.time_zone?.for,
      };

      if (this.userId && this.userId !== 'all') {
        params.user_id = this.userId;
      }
      if (this.teamId) {
        params.team_id = this.teamId;
      }
      if (this.remoteVsOffice) {
        params.remote_vs_office = true;
      }

      return params;
    },
    buildPDFQueryParams() {
      const sort = this.widgets?.leaderboard?.sort;
      const params = {
        from: this.filters.date?.from,
        to: this.filters.date?.to,
        time_zone: this.filters.time_zone || this.organization.time_zone?.for,
        period: this.filters.date?.period || '',
      };

      if (this.userId && this.userId !== 'all') {
        params.user_id = this.userId;
      }
      if (this.teamId) {
        params.team_id = this.teamId;
      }
      if (sort?.field && sort?.direction) {
        params.leaderboard_sort_by = sort.field;
        params.leaderboard_sort_order = sort.direction;
      }
      if (!this.leaderboardShowMetrics) {
        params.leaderboard_show_metrics = 'false';
      }
      if (!this.showBenchmarksAverages) {
        params.show_benchmarks = 'false';
      }

      return params;
    },
    handleLazyInput(value) {
      this.setMembersWithPreviousView(value, 'all_user_id');
    },
    handleTimelineInput(title, value) {
      if (!value || !value.id) {
        return;
      }

      // `time_zone` matters only for the user direction; the team handler ignores the extra arg.
      this.$emit(`timeline-${title}-selected`, value.id, value.time_zone);
    },
    timelineBackButtonLink() {
      this.$emit(`timeline-all-selected`);
    },
  },
};
</script>
